import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  Optional,
  Renderer2
} from '@angular/core';
import { NgControl } from '@angular/forms';
import nspell from 'nspell';

@Directive({
  selector: '[appSpellcheck]'
})
export class SpellcheckDirective implements OnInit {

  @Input() lang: 'en' | 'de' = 'en';

  private spell!: any;
  private highlightDiv!: HTMLElement;
  private suggestionBox!: HTMLElement;
  private debounceTimer: any;
  private currentWordRange!: { start: number; end: number };
  private userTyped = false;
  private outerWrapper
  constructor(
    private el: ElementRef<HTMLInputElement | HTMLTextAreaElement>,
    private renderer: Renderer2,
    @Optional() private ngControl: NgControl
  ) { }

  async ngOnInit() {
    this.loadLanguage();
    await this.loadDictionary();
    this.disableBrowserSpellcheck();
    this.createHighlightLayer();
    this.createSuggestionBox();
    this.ngControl?.valueChanges?.subscribe(value => {
      if (!value) {
        this.highlightDiv.innerHTML = '';
        this.hideSuggestions();
      }
    });
  }

  private loadLanguage() {
    const stored = (localStorage.getItem('asagoDefaultUserLang') || 'en').toLowerCase();
    this.lang = stored === 'de' ? 'de' : 'en';
  }

  private async loadDictionary() {
    const aff = await fetch(`assets/dict/${this.lang}.aff`).then(r => r.text());
    const dic = await fetch(`assets/dict/${this.lang}.dic`).then(r => r.text());
    this.spell = nspell({ aff, dic });
  }

  private disableBrowserSpellcheck() {
    this.el.nativeElement.setAttribute('spellcheck', 'false');
  }

  @HostListener('input')
  onInput() {
    this.userTyped = true;
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.runSpellcheck(true);
    }, 200);
  }

  @HostListener('blur')
  onBlur() {
    this.hideSuggestions();
    const text = this.el.nativeElement.value;
    this.highlightDiv.innerHTML = this.getHighlightedHTML(text, true);

  }

  private runSpellcheck(fromUser = false) {
    const text = this.getValue();
    if (!text.trim()) {
      this.highlightDiv.innerHTML = '';
      this.hideSuggestions();
      return;
    }
    this.highlightDiv.innerHTML = this.getHighlightedHTML(text);
    if (fromUser && this.userTyped) {
      this.detectWordForSuggestions(text);
    }
  }

  private createHighlightLayer() {
    const input = this.el.nativeElement;
    const parent = input.parentElement!;

    // OUTER container (allows popup to escape)
    const outer = this.renderer.createElement('div');
    Object.assign(outer.style, {
      position: 'relative',
      width: '100%',
      overflow: 'visible'
    });

    parent.insertBefore(outer, input);

    // INNER wrapper (can be overflow hidden)
    const wrapper = this.renderer.createElement('div');
    Object.assign(wrapper.style, {
      position: 'relative',
      width: '100%',
      overflow: 'hidden'
    });

    outer.appendChild(wrapper);
    wrapper.appendChild(input);

    const style = getComputedStyle(input);

    this.highlightDiv = this.renderer.createElement('div');
    Object.assign(this.highlightDiv.style, {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      overflow: 'hidden',
      fontFamily: style.fontFamily,
      fontSize: style.fontSize,
      fontWeight: style.fontWeight,
      letterSpacing: style.letterSpacing,
      lineHeight: 1,
      padding: `calc(${style.paddingTop}) ${style.paddingRight} calc(${style.paddingBottom} + 2px) ${style.paddingLeft}`,
      boxSizing: 'border-box',
      color: 'transparent'
    });

    input.style.background = 'transparent';
    wrapper.appendChild(this.highlightDiv);

    input.addEventListener('scroll', () => {
      this.highlightDiv.scrollTop = input.scrollTop;
      this.highlightDiv.style.left = `-${input.scrollLeft}px`;
    });
    this.outerWrapper = outer;
  }


  private detectWordForSuggestions(text: string) {
    const input = this.el.nativeElement;
    const pos = input.selectionStart ?? 0;
    const left = text.slice(0, pos).search(/\b\w+$/);
    const right = text.slice(pos).search(/\W/);
    if (left === -1) return this.hideSuggestions();
    const start = left;
    const end = right === -1 ? text.length : pos + right;
    const word = text.substring(start, end);
    if (!word || this.spell.correct(word)) {
      return this.hideSuggestions();
    }
    this.currentWordRange = { start, end };
    this.showSuggestions(this.spell.suggest(word).slice(0, 5));
  }

  private showSuggestions(words: string[]) {
    if (!words.length) return this.hideSuggestions();

    this.suggestionBox.innerHTML = '';

    words.forEach(word => {
      const div = this.renderer.createElement('div');
      div.innerText = word;
      Object.assign(div.style, {
        padding: '6px 10px',
        cursor: 'pointer'
      });

      div.addEventListener('mousedown', e => {
        e.preventDefault();
        this.applySuggestion(word);
      });

      this.suggestionBox.appendChild(div);
    });

    // ✅ Calculate position of the word, not the input
    const position = this.getWordPosition();
    this.suggestionBox.style.left = `${position.left}px`;
    this.suggestionBox.style.top = `${position.top}px`;
    this.suggestionBox.style.display = 'block';
  }

  private getWordPosition(): { left: number; top: number } {
    const input = this.el.nativeElement;
    const { start } = this.currentWordRange;

    // Create a temporary span to measure text position
    const tempSpan = document.createElement('span');
    const style = getComputedStyle(input);

    Object.assign(tempSpan.style, {
      position: 'absolute',
      visibility: 'hidden',
      whiteSpace: 'pre',
      fontFamily: style.fontFamily,
      fontSize: style.fontSize,
      fontWeight: style.fontWeight,
      letterSpacing: style.letterSpacing,
      padding: '0',
      margin: '0',
      border: 'none'
    });

    // Get text before the word
    const textBeforeWord = input.value.substring(0, start);
    tempSpan.textContent = textBeforeWord;

    document.body.appendChild(tempSpan);
    const textWidth = tempSpan.offsetWidth;
    document.body.removeChild(tempSpan);

    // Get input position and calculate word position
    const inputRect = input.getBoundingClientRect();
    const inputStyle = getComputedStyle(input);
    const paddingLeft = parseFloat(inputStyle.paddingLeft);
    const paddingTop = parseFloat(inputStyle.paddingTop);

    const left = inputRect.left + paddingLeft + textWidth - input.scrollLeft;
    const lineHeight = parseFloat(inputStyle.lineHeight) || parseFloat(inputStyle.fontSize) * 1.2;
    const top = inputRect.top + paddingTop + lineHeight + 2;

    return { left, top };
  }


  private applySuggestion(word: string) {
    const { start, end } = this.currentWordRange;
    const value = this.getValue();
    const newValue = value.slice(0, start) + word + value.slice(end);
    this.setValue(newValue);
    const cursor = start + word.length;
    this.el.nativeElement.setSelectionRange(cursor, cursor);
    this.hideSuggestions();
    this.runSpellcheck(true);
  }

  private hideSuggestions() {
    this.suggestionBox.style.display = 'none';
  }

  private getValue(): string {
    return this.el.nativeElement.value || '';
  }

  private setValue(val: string) {
    this.el.nativeElement.value = val;
    this.el.nativeElement.dispatchEvent(new Event('input'));
  }

  private escapeHtml(text: string): string {
    return text.replace(/[&<>"']/g, m =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[m]!)
    );
  }

  private getHighlightedHTML(text: string, force = false): string {
    const escaped = this.escapeHtml(text);
    const cursorPos = this.el.nativeElement.selectionStart ?? 0;

    return escaped.replace(/\b[\p{L}']+\b/gu, (word, offset) => {
      const wordStart = offset;
      const wordEnd = offset + word.length;

      // 1️⃣ Skip current typing word
      if (!force && cursorPos >= wordStart && cursorPos <= wordEnd) return word;

      // 3️⃣ Dictionary check
      return this.spell.correct(word)
        ? word
        : `<span class="spell-error">${word}</span>`;
    });
  }



  private createSuggestionBox() {
    this.suggestionBox = this.renderer.createElement('div');
    Object.assign(this.suggestionBox.style, {
      position: 'absolute',
      background: '#fff',
      border: '1px solid #ccc',
      fontSize: '13px',
      display: 'none',
      zIndex: '9999',
      boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
      borderRadius: '4px',
      minWidth: '120px'
    });
    document.body.append(this.suggestionBox);
  }

  @HostListener('mouseup')
  @HostListener('keyup')
  onSelectionChange() {
    this.handleSelection();
  }
  private handleSelection() {
    const input = this.el.nativeElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;

    // No selection or caret only
    if (start === end) {
      this.hideSuggestions();
      return;
    }

    const text = this.getValue();
    const selectedText = text.substring(start, end).trim();

    // Allow only single word selections
    if (!selectedText || /\s/.test(selectedText)) {
      this.hideSuggestions();
      return;
    }

    // If word is correct, don't show suggestions
    if (this.spell.correct(selectedText)) {
      this.hideSuggestions();
      return;
    }

    // Store range for replacement
    this.currentWordRange = { start, end };

    // Show suggestions
    const suggestions = this.spell.suggest(selectedText).slice(0, 5);
    this.showSuggestions(suggestions);
  }

}
